import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import useReactRouter from "use-react-router";
import { FIND_MY_ORDERS } from "../../../graphql/findOrder/query";

/**
 * STAGE B — the buyer's self-service order check.
 *
 * Pinned in a live. A buyer whose bill has not arrived has exactly one option today:
 * comment again. That is the single most damaging thing they can do — it creates a
 * duplicate order AND burns a second private-reply slot on a Page whose 750/hour quota is
 * already the binding constraint. This page exists to give them somewhere else to look in
 * the same second they would otherwise re-comment.
 *
 * MOBILE FIRST, and not as a style preference: every viewer is inside the Facebook app on
 * a phone, mid-live, in a hurry. One input, one button, large tap targets, no navigation,
 * no login. The page must be usable one-handed while a live is playing.
 *
 * It deliberately shows NO phone, address, item detail or payable link — the API does not
 * return them. Everything that identifies a person or moves money is behind Messenger,
 * which authenticates the buyer by PSID for free.
 */

const C = {
  ink: "#16181c",
  dim: "#6b7280",
  line: "#e5e7eb",
  bg: "#f5f6f8",
  card: "#ffffff",
  brand: "#1a66be",
  ok: "#0f7b52",
  okBg: "#e7f5ee",
  warn: "#9a5f00",
  warnBg: "#fdf3e0",
  stop: "#a32b22",
  stopBg: "#fbeae8",
};

/**
 * The five states a buyer can be in, in their own language, each ending in the ONE action
 * that helps. A generic "please try again" would send them back to commenting, which is
 * the behaviour this page exists to prevent — so there is no such message here.
 */
const ADVICE = {
  GREET_PAGE: {
    tone: "warn",
    title: "ກະລຸນາທັກແຊັດຫາເພຈກ່ອນ",
    body:
      "ອໍເດີ້ຂອງທ່ານສຳເລັດແລ້ວ ພຽງແຕ່ພວກເຮົາຍັງສົ່ງຂໍ້ຄວາມຫາທ່ານບໍ່ໄດ້ເທື່ອ. " +
      "ກົດປຸ່ມລຸ່ມນີ້ ແລ້ວທັກຫາເພຈ (ພິມຫຍັງກໍໄດ້) ໃບບິນຈະສົ່ງເຂົ້າແຊັດໃຫ້ທັນທີ. " +
      "ທັກເທື່ອດຽວພໍ — ບໍ່ຕ້ອງ CF ຊ້ຳອີກ ເພາະອໍເດີ້ນີ້ຍັງຢູ່ຄືເກົ່າ.",
    cta: "ສົ່ງໃບບິນເຂົ້າແຊັດ",
  },
  SHOP_SETTING: {
    tone: "warn",
    title: "ຮ້ານກຳລັງແກ້ໄຂການຕັ້ງຄ່າ",
    body:
      "ອໍເດີ້ຂອງທ່ານສຳເລັດແລ້ວ ແລະ ບໍ່ໄດ້ຫາຍໄປ. ທາງຮ້ານກຳລັງແກ້ໄຂ ແລ້ວຈະສົ່ງໃບບິນໃຫ້ທ່ານ. " +
      "ບໍ່ຕ້ອງ CF ຊ້ຳອີກ.",
    cta: null,
  },
  CANNOT_MESSAGE: {
    tone: "warn",
    title: "ສົ່ງຂໍ້ຄວາມຫາທ່ານບໍ່ໄດ້",
    body:
      "ອໍເດີ້ຂອງທ່ານສຳເລັດແລ້ວ ແຕ່ Facebook ບໍ່ອະນຸຍາດໃຫ້ສົ່ງຂໍ້ຄວາມຫາທ່ານ. " +
      "ກະລຸນາທັກຫາເພຈຂອງຮ້ານໂດຍກົງເພື່ອຮັບໃບບິນ. ບໍ່ຕ້ອງ CF ຊ້ຳອີກ.",
    cta: null,
  },
  SENDING: {
    tone: "warn",
    title: "ກຳລັງສົ່ງໃບບິນ",
    body:
      "ອໍເດີ້ຂອງທ່ານສຳເລັດແລ້ວ ແລະ ໃບບິນກຳລັງຈະສົ່ງໄປຫາທ່ານ. ກະລຸນາລໍຖ້າ ແລະ ບໍ່ຕ້ອງ CF ຊ້ຳອີກ.",
    cta: null,
  },
};

const money = (n, cur) =>
  `${new Intl.NumberFormat("en-US").format(Math.round(Number(n) || 0))} ${cur === "LAK" || !cur ? "ກີບ" : cur}`;

/**
 * The moment a search ran, written out in full.
 *
 * Seconds and the timezone are both deliberate: this string gets compared against a server log
 * by someone deciding whether a buyer really did CF before the bill failed, and "14:05" on its
 * own cannot settle that.
 */
const stampOf = (d) => {
  try {
    const dt = new Date(d);
    const pad = (n) => String(n).padStart(2, "0");
    const off = -dt.getTimezoneOffset();
    const sign = off >= 0 ? "+" : "-";
    const tz = `GMT${sign}${Math.floor(Math.abs(off) / 60)}`;
    return (
      `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ` +
      `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())} ${tz}`
    );
  } catch (_) {
    return "";
  }
};

const timeOf = (d) => {
  try {
    return new Date(d).toLocaleString("lo-LA", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
};

function FindOrder() {
  const { match } = useReactRouter();
  /**
   * The SHOP id. Not a live id — the shop pins one link and buyers order from posts as
   * well as broadcasts. Not a Page id either: a Facebook Page is not unique to a shop in
   * this data model (one production page is claimed by fifteen shops), so a page-scoped
   * lookup would show one shop's buyers another shop's orders.
   */
  const shopId = match?.params?.shopId;
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);

  const [run, { data, loading }] = useLazyQuery(FIND_MY_ORDERS, {
    fetchPolicy: "network-only",
  });

  /**
   * When this search ran. Shown next to the results so a buyer, and more importantly a shop's
   * support staff reading a screenshot, can tell WHEN the answer was true. "No order found" an
   * hour ago and "no order found" just now are different facts, and without a timestamp a
   * screenshot cannot distinguish them.
   */
  const [searchedAt, setSearchedAt] = useState(null);

  /**
   * Fetch the shop identity immediately, with an empty query.
   *
   * The buyer must see WHICH shop they are checking before they type. Otherwise a wrong pinned
   * link and a genuine empty result look identical — and in both cases the buyer concludes their
   * order is gone, which is the one thing this page exists to prevent. The resolver answers an
   * empty query with the shop name and no rows.
   */
  useEffect(() => {
    if (shopId) run({ variables: { shopId, nameQuery: "" } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const result = data?.findMyOrders;
  //* Page name first: a shop may run several Pages, and the Page is what the buyer was
  //* actually watching. Falls back to the shop name, then to nothing rather than a
  //* placeholder — an empty header is honest, "Unknown shop" is alarming.
  const shopLabel = result?.pageName || result?.shopName || null;
  const orders = result?.orders || [];
  const tooShort = name.trim().length > 0 && name.trim().length < 3;

  const submit = (e) => {
    //* Plain guard rather than e?.preventDefault?.(): this app builds on react-scripts
    //* 3.4.3, whose eslint parser does not understand OPTIONAL CALL syntax and reports it
    //* as no-unused-expressions, failing the build. Optional property access (?.) is fine
    //* on this toolchain; only ?.() is not.
    if (e && e.preventDefault) e.preventDefault();
    setTouched(true);
    if (name.trim().length < 3 || !shopId) return;
    setSearchedAt(new Date());
    run({ variables: { shopId, nameQuery: name.trim() } });
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <header style={S.header}>
          <div style={S.logo}>4B</div>
          <h1 style={S.h1}>ກວດສອບອໍເດີ້ຂອງຂ້ອຍ</h1>
          {/*
            Frames the whole page before anything else is read: the ORDER is never the
            thing at risk, only the message. A buyer who absorbs that one sentence has no
            reason to re-comment, whatever the rows below say.
          */}
          <p style={S.sub}>
            ອໍເດີ້ທີ່ CF ແລ້ວຈະຖືກບັນທຶກສະເໝີ — ໜ້ານີ້ບອກພຽງວ່າ
            <strong> ໃບບິນສົ່ງເຖິງທ່ານແລ້ວຫຼືຍັງ</strong>
          </p>
          <p style={S.sub}>
            ພິມຊື່ເຟສບຸກຂອງທ່ານ (ຢ່າງໜ້ອຍ 3 ຕົວອັກສອນ)
          </p>
          {/*
            Which live this is. A buyer who follows several shops needs to SEE the shop
            name before the numbers below mean anything — otherwise a wrong pinned link and
            an empty result look identical. Rendered only once the query has answered, so
            the header does not flicker on first load.
          */}
          {/*
            Rendered as soon as the shop is known, which is now on first load rather than
            after a search — see the mount effect above.
          */}
          {shopLabel ? (
            <div style={S.scope}>
              <span style={S.scopeDim}>ກຳລັງກວດອໍເດີ້ຂອງຮ້ານ</span>
              <strong style={S.scopeName}>{shopLabel}</strong>
              {result?.windowHours ? (
                <span style={S.scopeDim}>
                  ສະແດງອໍເດີ້ພາຍໃນ {result.windowHours} ຊົ່ວໂມງຜ່ານມາ
                </span>
              ) : null}
              {/*
                The exact moment of THIS search. Support staff are usually shown a photo of
                this screen, so the timestamp has to be in the picture or it is lost.
              */}
              {searchedAt ? (
                <span style={S.stamp}>ຜົນການຄົ້ນຫາ ນທີ {stampOf(searchedAt)}</span>
              ) : null}
            </div>
          ) : null}
        </header>

        <form onSubmit={submit} style={S.form}>
          {/*
            type="search" + enterKeyHint gives the phone keyboard a "search" key, so the
            whole interaction is one thumb and one tap. autoFocus is deliberate: the buyer
            arrived here to type, and every extra tap during a live is a re-comment risk.
          */}
          <input
            style={S.input}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoFocus
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ຊື່ເຟສບຸກຂອງທ່ານ"
            aria-label="ຊື່ເຟສບຸກຂອງທ່ານ"
          />
          <button
            type="submit"
            style={{ ...S.btn, opacity: name.trim().length < 3 || loading ? 0.55 : 1 }}
            disabled={name.trim().length < 3 || loading}
          >
            {loading ? "ກຳລັງຄົ້ນຫາ..." : "ຄົ້ນຫາອໍເດີ້"}
          </button>
          {touched && tooShort && (
            <p style={S.hint}>ກະລຸນາພິມຢ່າງໜ້ອຍ 3 ຕົວອັກສອນ</p>
          )}
        </form>

        {result?.error === "RATE_LIMITED" && (
          <Note tone="warn" title="ຄົ້ນຫາຫຼາຍເກີນໄປ" body="ກະລຸນາລໍຖ້າ 1 ນາທີ ແລ້ວລອງໃໝ່." />
        )}
        {result?.error === "SHOP_NOT_FOUND" && (
          <Note tone="stop" title="ບໍ່ພົບຮ້ານນີ້" body="ກະລຸນາກົດລິ້ງຈາກຄອມເມັ້ນປັກມຸດຂອງຮ້ານອີກຄັ້ງ." />
        )}

        {result && !result.error && orders.length === 0 && (
          <Note
            tone="warn"
            title="ບໍ່ພົບອໍເດີ້"
            body="ລອງພິມຊື່ໃຫ້ຄືກັບຊື່ເຟສບຸກຂອງທ່ານ. ໜ້ານີ້ສະແດງສະເພາະອໍເດີ້ຫຼ້າສຸດ — ຖ້າ CF ດົນແລ້ວ ກະລຸນາທັກແຊັດຫາເພຈຂອງຮ້ານ."
          />
        )}

        {orders.map((o, i) => (
          <OrderCard key={`${o.code || "og"}-${i}`} o={o} />
        ))}

        {result?.truncated && (
          <p style={S.hint}>
            ພົບຫຼາຍລາຍການ — ກະລຸນາພິມຊື່ໃຫ້ຍາວຂຶ້ນເພື່ອຫາອໍເດີ້ຂອງທ່ານ
          </p>
        )}

        {/*
          Said on every state, including success. The whole purpose of the page is that a
          buyer never concludes their order vanished, so the reassurance cannot be
          conditional on the search having worked.
        */}
        {/*
          Anti-impersonation. Anyone watching the live can see a buyer's real name in the
          comments, search it here, and learn an amount and a partial code - enough to pose
          as the shop in a private message. The page cannot prevent that, but telling every
          buyer the one rule that defeats it costs nothing and is the single most valuable
          line on the page.
        */}
        <div style={S.scam}>
          <strong style={S.scamTitle}>⚠️ ລະວັງການຫຼອກລວງ</strong>
          ທາງຮ້ານ ແລະ 4B <strong>ຈະບໍ່ທັກໄປຂໍໃຫ້ໂອນເງິນ</strong> ຜ່ານຂໍ້ຄວາມສ່ວນຕົວ.
          ຊຳລະເງິນຜ່ານລິ້ງໃບບິນທີ່ສົ່ງມາໃນແຊັດຂອງເພຈຮ້ານເທົ່ານັ້ນ.
        </div>

        <p style={S.foot}>
          ທຸກອໍເດີ້ທີ່ CF ແລ້ວຈະຖືກບັນທຶກໄວ້ສະເໝີ ເຖິງແມ່ນວ່າຂໍ້ຄວາມຍັງບໍ່ທັນສົ່ງເຖິງທ່ານ.
          <br />
          <strong>ບໍ່ຕ້ອງ CF ຊ້ຳອີກ</strong> ເພາະຈະກາຍເປັນອໍເດີ້ຊ້ຳກັນ.
          <br />
          {/*
            A live belongs to exactly one shop, so this page can only ever answer for one.
            Saying so turns a confusing empty result - "I ordered from three shops, where are
            the others?" - into an obvious next step.
          */}
          ໜ້ານີ້ກວດໄດ້ສະເພາະເພຈຮ້ານນີ້ເທົ່ານັ້ນ. ຖ້າ CF ຫຼາຍຮ້ານ ກະລຸນາກົດລິ້ງປັກມຸດຂອງແຕ່ລະຮ້ານ.
        </p>
      </div>
    </div>
  );
}

function OrderCard({ o }) {
  const delivered = o.deliveryState === "SENT";
  const advice = !delivered && o.advice ? ADVICE[o.advice] : null;

  return (
    <div style={S.card}>
      <div style={S.cardTop}>
        <div>
          <div style={S.name}>{o.maskedName}</div>
          <div style={S.meta}>
            {o.code ? `#${o.code}` : ""} {o.createdAt ? `· ${timeOf(o.createdAt)}` : ""}
          </div>
        </div>
        <Badge ok={delivered} />
      </div>

      {/*
        Shown on EVERY card, delivered or not. The order and the notification are two
        different things, and only one of them can fail — a buyer who leaves this page
        unsure which is which is a buyer who re-comments.
      */}
      <div style={S.confirmed}>✅ ຮັບອໍເດີ້ຂອງທ່ານແລ້ວ — ອໍເດີ້ນີ້ຖືກບັນທຶກໄວ້ຮຽບຮ້ອຍ</div>

      <div style={S.row}>
        <span style={S.dim}>ຈຳນວນລາຍການ</span>
        <span style={S.strong}>{o.itemCount}</span>
      </div>
      <div style={S.row}>
        <span style={S.dim}>ລວມເງິນ</span>
        <span style={S.strong}>{money(o.totalPrice, o.currency)}</span>
      </div>

      {delivered && (
        <div style={{ ...S.note, background: C.okBg, borderColor: C.ok, color: C.ok }}>
          ສົ່ງໃບບິນເຂົ້າແຊັດຂອງທ່ານແລ້ວ — ກະລຸນາເປີດ Messenger ເພື່ອເບິ່ງໃບບິນ
        </div>
      )}

      {advice && (
        <div style={{ ...S.note, background: C.warnBg, borderColor: C.warn, color: C.warn }}>
          <strong style={S.noteTitle}>{advice.title}</strong>
          {advice.body}
        </div>
      )}

      {/*
        The greet button is the one control that changes anything. Tapping it opens the
        24-hour Messenger window, after which the bill goes out on the 300/sec Send API
        instead of waiting for comment quota — so the buyer's recovery action is also the
        capacity fix. Full width and last, because it is the only thing to do here.
      */}
      {advice?.cta && o.greetLink && (
        <a href={o.greetLink} style={S.cta} target="_blank" rel="noopener noreferrer">
          {advice.cta}
        </a>
      )}
    </div>
  );
}

/**
 * The badge describes the MESSAGE, never the order.
 *
 * "ລໍຖ້າສົ່ງ" on its own reads as "the order is pending", which is the single most
 * expensive misreading this page can produce: a buyer who believes their order did not go
 * through comments again, creating a duplicate and burning a second private-reply slot.
 * Naming the thing that is waiting — the message — removes the ambiguity.
 */
const Badge = ({ ok }) => (
  <span
    style={{
      ...S.badge,
      color: ok ? C.ok : C.warn,
      background: ok ? C.okBg : C.warnBg,
    }}
  >
    {ok ? "ສົ່ງຂໍ້ຄວາມແລ້ວ" : "ລໍຖ້າສົ່ງຂໍ້ຄວາມ"}
  </span>
);

const Note = ({ tone, title, body }) => (
  <div
    style={{
      ...S.card,
      borderLeft: `4px solid ${tone === "stop" ? C.stop : C.warn}`,
    }}
  >
    <strong style={{ ...S.noteTitle, color: tone === "stop" ? C.stop : C.warn }}>{title}</strong>
    <span style={S.dim}>{body}</span>
  </div>
);

/**
 * Inline styles rather than the admin app's Material-UI / react-bootstrap stack: this page
 * is loaded by buyers on phones inside the Facebook in-app browser, often on slow
 * connections, and it has no reason to pull a component library in to render one input and
 * a list of cards.
 */
const S = {
  page: {
    minHeight: "100vh",
    background: C.bg,
    fontFamily: '"Noto Sans Lao", system-ui, -apple-system, sans-serif',
    color: C.ink,
    padding: "16px 12px 40px",
  },
  //* Caps the line length on a tablet or desktop; on a phone it is simply full width.
  wrap: { maxWidth: 520, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 },
  header: { textAlign: "center", padding: "8px 0 4px" },
  logo: {
    width: 44, height: 44, borderRadius: 12, margin: "0 auto 10px",
    background: "linear-gradient(47deg, rgba(26,102,190,1) 0%, rgba(90,84,162,1) 100%)",
    color: "#fff", fontWeight: 700, fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  h1: { fontSize: 22, margin: "0 0 6px", fontWeight: 700, lineHeight: 1.3 },
  sub: { fontSize: 14, color: C.dim, margin: 0, lineHeight: 1.6 },
  form: { display: "flex", flexDirection: "column", gap: 10, marginTop: 4 },
  input: {
    //* 16px minimum: anything smaller makes iOS Safari zoom the whole page on focus,
    //* which on a live-stream link feels like the page broke.
    fontSize: 16, padding: "14px 16px", borderRadius: 12,
    border: `1px solid ${C.line}`, outline: "none", width: "100%",
    fontFamily: "inherit", background: C.card,
  },
  btn: {
    //* 52px tall: a comfortable thumb target one-handed, per platform guidance.
    fontSize: 16, fontWeight: 600, padding: "15px 16px", borderRadius: 12,
    border: "none", color: "#fff", width: "100%", cursor: "pointer",
    background: "linear-gradient(47deg, rgba(26,102,190,1) 0%, rgba(90,84,162,1) 100%)",
    fontFamily: "inherit",
  },
  card: {
    background: C.card, borderRadius: 14, padding: 16,
    border: `1px solid ${C.line}`, display: "flex", flexDirection: "column", gap: 10,
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 },
  name: { fontSize: 17, fontWeight: 700 },
  meta: { fontSize: 12.5, color: C.dim, marginTop: 3 },
  badge: { fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 999, whiteSpace: "nowrap" },
  row: { display: "flex", justifyContent: "space-between", fontSize: 15 },
  dim: { color: C.dim, fontSize: 14, lineHeight: 1.65 },
  strong: { fontWeight: 600 },
  note: { fontSize: 13.5, lineHeight: 1.65, padding: "11px 13px", borderRadius: 10, border: "1px solid" },
  noteTitle: { display: "block", marginBottom: 4, fontSize: 14.5 },
  cta: {
    display: "block", textAlign: "center", textDecoration: "none",
    fontSize: 16, fontWeight: 700, padding: "15px 16px", borderRadius: 12,
    color: "#fff", background: C.brand,
  },
  hint: { fontSize: 13, color: C.dim, textAlign: "center", margin: "2px 0" },
  scope: {
    marginTop: 12, padding: "10px 14px", borderRadius: 10,
    background: C.card, border: `1px solid ${C.line}`,
    display: "flex", flexDirection: "column", gap: 2,
  },
  scopeDim: { fontSize: 12, color: C.dim },
  //* Monospace so digits line up when two screenshots are compared side by side.
  stamp: {
    fontSize: 11.5, color: C.dim, marginTop: 4, fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
  scopeName: { fontSize: 15.5, fontWeight: 700 },
  scam: {
    fontSize: 12.5, lineHeight: 1.7, padding: "11px 13px", borderRadius: 10,
    background: C.stopBg, border: `1px solid ${C.stop}`, color: C.stop, marginTop: 6,
  },
  scamTitle: { display: "block", marginBottom: 3, fontSize: 13.5 },
  confirmed: {
    fontSize: 13.5, fontWeight: 600, color: C.ok, background: C.okBg,
    padding: "9px 12px", borderRadius: 9, lineHeight: 1.55,
  },
  foot: { fontSize: 12.5, color: C.dim, textAlign: "center", lineHeight: 1.7, marginTop: 8 },
};

export default FindOrder;
